/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailCollabParts.js
 * @description
 * Small presentational pieces of shared-mailbox collaboration, shared by the list rows, the
 * reader and the settings screen: {@link CollabAvatar} (avatar + online dot),
 * {@link RepliedByLine} (the "replied by" requirement), {@link CollabRowMeta} (assignee chip, tag
 * chips, Done badge, snooze clock, "Unassigned" pill + Claim) and {@link CollabSheetRow}.
 * Theme tokens only (`useManagementTheme`) so dark mode just works.
 */

import React from 'react';
import {Image, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import {LabelChip, MailAssigneeLine, MailAvatar} from './MailUiKit';
import {formatRelativeTime, resolveTags} from '../../helpers/mailCollabUtils';
import {chatSummary} from '../../helpers/mailThreadChat';

const isRemoteUrl = (url) => /^https?:\/\//i.test(String(url || ''));

/**
 * Avatar of a colleague with an optional online dot.
 *
 * @param {{user?: {name?: string, avatarUrl?: string, online?: boolean}, size?: number, showOnline?: boolean}} props Props.
 * @returns {JSX.Element} Avatar.
 */
export const CollabAvatar = ({user, size = 24, showOnline = false}) => {
    const m = useManagementTheme();
    const dot = Math.max(7, Math.round(size * 0.32));

    return (
        <View style={{width: size, height: size}}>
            {isRemoteUrl(user?.avatarUrl) ? (
                <Image
                    source={{uri: user.avatarUrl}}
                    style={{width: size, height: size, borderRadius: size / 2, backgroundColor: m.colors.surfaceAlt}}
                />
            ) : (
                <MailAvatar who={{name: user?.name || '?'}} size={size} />
            )}
            {showOnline && (
                <View
                    style={[
                        styles.onlineDot,
                        {
                            width: dot,
                            height: dot,
                            borderRadius: dot / 2,
                            borderColor: m.colors.surface,
                            backgroundColor: user?.online ? m.colors.success : m.colors.muted,
                        },
                    ]}
                />
            )}
        </View>
    );
};

/**
 * "↩ <avatar> <Name> replied · <relative time>" line, visible to every member of the mailbox.
 *
 * @param {{lastReply?: {name?: string, avatarUrl?: string, at?: string}|null, banner?: boolean}} props
 *        `banner` renders the larger reader banner variant.
 * @returns {JSX.Element|null} Line, or null when nobody replied yet.
 */
export const RepliedByLine = ({lastReply, banner = false}) => {
    const m = useManagementTheme();
    const {t, i18n} = useTranslation();

    if (!lastReply) {
        return null;
    }

    const when = formatRelativeTime(lastReply.at, t, i18n?.language || 'en');
    const name = lastReply.name || t('mail_collab_someone');
    const text = `${t('mail_collab_replied_by', {name})}${when ? ` · ${when}` : ''}`;

    return (
        <View
            style={[
                styles.repliedRow,
                banner && [styles.repliedBanner, {backgroundColor: m.colors.chipBg, borderColor: m.colors.borderSoft}],
            ]}
        >
            <Icon name="reply" size={banner ? 16 : 12} color={m.colors.primary} />
            <CollabAvatar user={lastReply} size={banner ? 22 : 16} />
            <Text style={[styles.repliedText, banner && styles.repliedTextBanner, {color: banner ? m.colors.heading : m.colors.subText}]} numberOfLines={1}>
                {text}
            </Text>
        </View>
    );
};

/**
 * Collaboration meta of a list row: replied-by line, assignee chip / "Unassigned" pill + Claim,
 * up to three team-tag chips (+n), Done badge and snooze clock.
 *
 * @param {Object} props Props.
 * @param {Object|null} props.state Thread state (`null` = no shared state yet).
 * @param {Object[]} props.mailboxTags Team tags of the row's mailbox.
 * @param {Object|null} [props.snooze] Pending snooze of the row.
 * @param {boolean} props.shared Row belongs to a shared mailbox.
 * @param {(() => void)|undefined} [props.onClaim] Claim handler (omit to hide the button).
 * @returns {JSX.Element|null} Meta block.
 */
export const CollabRowMeta = ({state, mailboxTags, snooze, shared, onClaim}) => {
    const m = useManagementTheme();
    const {t} = useTranslation();
    const tags = resolveTags(mailboxTags, state?.tags);
    const done = state?.status === 'done';
    const assignee = state?.assignee || null;
    const chat = chatSummary(state);

    if (!shared && !snooze) {
        return null;
    }

    return (
        <View style={styles.meta}>
            {shared && !!state?.lastReply && <RepliedByLine lastReply={state.lastReply} />}
            {shared && !!assignee && <MailAssigneeLine name={assignee.name} />}
            <View style={styles.metaRow}>
                {shared && !assignee && !done && (
                    <>
                        <View style={[styles.pill, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                            <Icon name="account-off-outline" size={12} color={m.colors.muted} />
                            <Text style={[styles.pillText, {color: m.colors.subText}]}>{t('mail_collab_unassigned')}</Text>
                        </View>
                        {!!onClaim && (
                            <TouchableOpacity
                                onPress={onClaim}
                                hitSlop={6}
                                activeOpacity={0.8}
                                style={[styles.claimBtn, {backgroundColor: m.colors.primary}]}
                                accessibilityRole="button"
                                accessibilityLabel={t('mail_collab_claim')}
                            >
                                <Text style={styles.claimText}>{t('mail_collab_claim')}</Text>
                            </TouchableOpacity>
                        )}
                    </>
                )}
                {shared && tags.slice(0, 3).map((tag) => <LabelChip key={tag.id} label={tag.name} color={tag.color} small />)}
                {shared && tags.length > 3 && (
                    <Text style={[styles.more, {color: m.colors.muted}]}>{`+${tags.length - 3}`}</Text>
                )}
                {shared && chat.count > 0 && (
                    <View
                        style={[
                            styles.pill,
                            chat.unread > 0
                                ? {backgroundColor: '#F97316', borderColor: '#F97316'}
                                : {backgroundColor: m.colors.chipBg, borderColor: m.colors.borderSoft},
                        ]}
                    >
                        <Icon name="forum-outline" size={12} color={chat.unread > 0 ? '#fff' : m.colors.subText} />
                        <Text style={[styles.pillText, {color: chat.unread > 0 ? '#fff' : m.colors.heading}]} numberOfLines={1}>
                            {chat.unread > 0 ? `${chat.count} - ${t('mail_chat_unread', {count: chat.unread})}` : String(chat.count)}
                        </Text>
                    </View>
                )}
                {shared && done && (
                    <View style={[styles.pill, {backgroundColor: m.colors.success + '22', borderColor: m.colors.success + '55'}]}>
                        <Icon name="check-circle-outline" size={12} color={m.colors.success} />
                        <Text style={[styles.pillText, {color: m.colors.success}]}>{t('mail_collab_done')}</Text>
                    </View>
                )}
                {!!snooze && (
                    <View style={[styles.pill, {backgroundColor: m.colors.chipBg, borderColor: m.colors.borderSoft}]}>
                        <Icon name={snooze.onlyIfNoReply ? 'reply-outline' : 'clock-outline'} size={12} color={m.colors.primary} />
                        {!!snooze.onlyIfNoReply && (
                            <Text style={[styles.pillText, {color: m.colors.primary}]} numberOfLines={1}>{t('mail_followup_chip')}</Text>
                        )}
                        <Text style={[styles.pillText, {color: m.colors.heading}]} numberOfLines={1}>
                            {new Date(snooze.until).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}
                        </Text>
                    </View>
                )}
            </View>
        </View>
    );
};

/**
 * Selectable row used by the bottom-sheet pickers (member / tag): leading node, title,
 * subtitle and a trailing check.
 *
 * @param {{leading?: React.ReactNode, title: string, subtitle?: string, selected?: boolean, onPress: () => void, last?: boolean, right?: React.ReactNode}} props Props.
 * @returns {JSX.Element} Row.
 */
export const CollabSheetRow = ({leading, title, subtitle, selected, onPress, last, right}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.7}
            style={[styles.sheetRow, {borderBottomColor: m.colors.borderSoft}, last && {borderBottomWidth: 0}]}
        >
            {leading}
            <View style={styles.sheetRowText}>
                <Text style={[styles.sheetRowTitle, {color: m.colors.heading}]} numberOfLines={1}>{title}</Text>
                {!!subtitle && <Text style={[styles.sheetRowSub, {color: m.colors.subText}]} numberOfLines={1}>{subtitle}</Text>}
            </View>
            {right}
            {selected ? <Icon name="check-circle" size={20} color={m.colors.primary} /> : null}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    onlineDot: {position: 'absolute', right: -2, bottom: -2, borderWidth: 1.5},
    repliedRow: {flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 3},
    repliedBanner: {paddingHorizontal: 12, paddingVertical: 9, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, marginTop: 0, gap: 8},
    repliedText: {fontSize: 11.5, fontWeight: '600', flexShrink: 1},
    repliedTextBanner: {fontSize: 13.5, fontWeight: '700'},
    meta: {marginTop: 4},
    metaRow: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 4},
    pill: {flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, borderWidth: StyleSheet.hairlineWidth, maxWidth: 160},
    pillText: {fontSize: 11, fontWeight: '700', flexShrink: 1},
    claimBtn: {paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999},
    claimText: {color: '#FFFFFF', fontSize: 11, fontWeight: '800'},
    more: {fontSize: 11, fontWeight: '700'},
    sheetRow: {flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth},
    sheetRowText: {flex: 1},
    sheetRowTitle: {fontSize: 15, fontWeight: '700'},
    sheetRowSub: {fontSize: 12, marginTop: 1},
});
